'use client';

import { useState, useEffect } from 'react';
import {
  getCookieConsent,
  saveCookieConsent,
  type CookieConsent,
} from '@/lib/cookie-consent';

/**
 * Hook to manage cookie consent state
 */
export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Load consent on mount
  useEffect(() => {
    const stored = getCookieConsent();
    setConsent(stored);
    // Show banner if no consent has been given
    setIsOpen(stored === null);
  }, []);

  // Listen for open-cookie-settings event (from Cookie Policy page)
  useEffect(() => {
    const handleOpenSettings = () => {
      setIsOpen(true);
      setShowSettings(true);
    };

    window.addEventListener('open-cookie-settings', handleOpenSettings);
    return () => {
      window.removeEventListener('open-cookie-settings', handleOpenSettings);
    };
  }, []);

  // Listen for consent updates from other tabs
  useEffect(() => {
    const handleConsentUpdate = (event: CustomEvent<CookieConsent | null>) => {
      setConsent(event.detail);
      if (event.detail) {
        setIsOpen(false);
        setShowSettings(false);
      }
    };

    window.addEventListener('cookie-consent-updated', handleConsentUpdate as EventListener);
    return () => {
      window.removeEventListener('cookie-consent-updated', handleConsentUpdate as EventListener);
    };
  }, []);

  const acceptAll = () => {
    const newConsent: Omit<CookieConsent, 'timestamp'> = {
      essential: true,
      analytical: true,
      marketing: true,
    };
    saveCookieConsent(newConsent);
    setConsent(getCookieConsent());
    setIsOpen(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const newConsent: Omit<CookieConsent, 'timestamp'> = {
      essential: true,
      analytical: false,
      marketing: false,
    };
    saveCookieConsent(newConsent);
    setConsent(getCookieConsent());
    setIsOpen(false);
    setShowSettings(false);
  };

  const savePreferences = (preferences: Omit<CookieConsent, 'timestamp'>) => {
    saveCookieConsent(preferences);
    setConsent(getCookieConsent());
    setIsOpen(false);
    setShowSettings(false);
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeBanner = () => {
    setIsOpen(false);
    setShowSettings(false);
  };

  return {
    consent,
    isOpen,
    showSettings,
    acceptAll,
    rejectAll,
    savePreferences,
    openSettings,
    closeBanner,
  };
}


